// Clean-up for the result sets of sp_get_hallviewauditTracker before the map
// reads them. The raw sets cannot be used as-is:
//
// data1 (service partner tasks) is a cross join against the hall table. Every
// stall row is repeated once per hall (19x in the 2026-09 capture), each copy
// carrying that hall's HallName/HallStatus/ZoneStatus, plus exact duplicates
// and thousands of rows with no StallName. 8082 rows hold 148 real records.
//
// data2 (exhibitor orders) mixes two kinds of row: the stall master (no
// ServiceID - one per stall, carrying its real Hall and Zone) and actual
// orders (ServiceID set), where each work update is its own row.
//
// The DB also keeps "H3-C2" (Hall 3) and "H3C2" (Hall 1) as different stalls,
// so a punctuation-stripped join alone attaches one hall's stall to another's
// booth. Joins to the drawing are therefore scoped by hall as well.

export const firstValue = (v) => (Array.isArray(v) ? v.find((x) => x != null && x !== "") : v);

// Loose join key: "H1-A1" and "H1A1" meet.
export const normalizeBoothKey = (name) =>
  String(name ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// Exact identity, spelled the way extractSvgLayoutData spells booth names.
export const stallNameKey = (name) =>
  String(name ?? "").trim().toUpperCase().replace(/\s*-\s*/g, "-");

export const hallNameKey = (name) =>
  String(name ?? "").trim().toLowerCase().replace(/\s+/g, " ");

export const zoneKeyOf = (hallName, zoneName) => {
  const z = hallNameKey(zoneName);
  return z ? `${hallNameKey(hallName)}::${z}` : "";
};

const orderStall = (r) => r.Stall || r.StallName;
const orderHall = (r) => r.Hall || r.HallName;

// Stall -> the hall/zone it really belongs to, from data2's per-stall rows.
export const buildStallHomes = (orderRows) => {
  const homes = new Map();
  (orderRows || []).forEach((r) => {
    const name = stallNameKey(orderStall(r));
    if (!name || homes.has(name) || !orderHall(r)) return;
    homes.set(name, {
      hall: orderHall(r),
      zone: r.Zone || r.ZoneName || null,
      zoneId: r.ZoneID != null ? r.ZoneID : null,
    });
  });
  return homes;
};

// "Overdue by 14 Days" -> 20160 (minutes). The SP writes a task that is not
// due yet as a negative figure ("Overdue by -5906 Minutes"); "" or anything
// unparseable is null.
const OVERDUE_UNIT_MINUTES = { minute: 1, hour: 60, day: 1440 };
export const overdueMinutes = (text) => {
  const m = /(-?\d+)\s*(minute|hour|day)/i.exec(String(text ?? ""));
  return m ? Number(m[1]) * OVERDUE_UNIT_MINUTES[m[2].toLowerCase()] : null;
};

// Collapse data1 to one row per task - stall x exhibitor x service x service
// partner. Two fan-outs are undone:
//   halls      the SP cross-joins the hall table, so each row repeats once per
//              hall; the copy naming the stall's own hall is kept (its
//              HallStatus/ZoneStatus are the true ones). A stall whose hall
//              cannot be established keeps one copy with the hall-dependent
//              columns cleared, so it still colours its booth but does not
//              vote on any hall's or zone's status.
//   checklist  a task repeats once per checklist item, the copies differing
//              only in Overdue. They become one row whose Overdue is the most
//              overdue item (null when none is past due, or the task is
//              Completed) and OverdueCount the number of items past due.
//
// Rows with no stall are the cross join's filler and are dropped - except in
// an other area (areaHallKeys: hallNameKey of halls flagged IsOtherArea),
// whose service partner tasks are for the area itself and have no stall.
export const cleanServicePartnerRows = (rows, stallHomes, areaHallKeys = new Set()) => {
  const groups = new Map();
  const areaTasks = new Map();
  (rows || []).forEach((r) => {
    const stall = stallNameKey(r.StallName);
    if (!stall) {
      if (areaHallKeys.has(hallNameKey(r.HallName))) {
        const id = JSON.stringify(r);
        if (!areaTasks.has(id)) {
          const mins = overdueMinutes(r.Overdue);
          areaTasks.set(id, { ...r, OverdueCount: mins != null && mins > 0 ? 1 : 0 });
        }
      }
      return;
    }
    const { HallName, HallStatus, ZoneStatus, Overdue, ...rest } = r;
    const id = JSON.stringify(rest);
    let g = groups.get(id);
    if (!g) {
      g = { stall, first: r, byHall: new Map(), overdue: new Map() };
      groups.set(id, g);
    }
    const hk = hallNameKey(HallName);
    if (!g.byHall.has(hk)) g.byHall.set(hk, r);
    // Per hall copy the same items repeat, so items are keyed by their text.
    const mins = overdueMinutes(Overdue);
    if (mins != null && mins > 0) g.overdue.set(String(Overdue).trim(), mins);
  });

  const withOverdue = (g, row) => {
    const done = String(firstValue(row.StallTaskStatus) || "").toLowerCase() === "completed" ||
      Number(firstValue(row.StallTaskStatus)) === 1;
    const items = done ? [] : Array.from(g.overdue.entries()).sort((a, b) => b[1] - a[1]);
    return { ...row, Overdue: items.length > 0 ? items[0][0] : null, OverdueCount: items.length };
  };

  const cleaned = [];
  groups.forEach((g) => {
    const home = stallHomes.get(g.stall);
    const homeKey = home ? hallNameKey(home.hall) : "";
    if (homeKey && g.byHall.has(homeKey)) {
      cleaned.push(withOverdue(g, g.byHall.get(homeKey)));
    } else if (!home && g.byHall.size === 1) {
      cleaned.push(withOverdue(g, g.first));
    } else if (home) {
      cleaned.push(withOverdue(g, { ...g.first, HallName: home.hall, HallStatus: null, ZoneStatus: null }));
    } else {
      cleaned.push(withOverdue(g, { ...g.first, HallName: null, HallStatus: null, ZoneStatus: null }));
    }
  });
  areaTasks.forEach((r) => cleaned.push(r));
  return cleaned;
};

// Halls flagged IsOtherArea in result set 0, as hallNameKey.
export const otherAreaHallKeys = (hallZoneMasterRows) =>
  new Set(
    (hallZoneMasterRows || [])
      .filter((h) => h && (h.IsOtherArea === true || h.IsOtherArea === 1))
      .map((h) => hallNameKey(h.HallName))
  );

// Only rows with a service are orders. Repeated work updates of one order are
// reduced to the latest (highest HMWorkupdationID).
export const cleanExhibitorOrderRows = (rows) => {
  const latest = new Map();
  (rows || []).forEach((r) => {
    if (r.ServiceID == null || !orderStall(r)) return;
    const id = [
      stallNameKey(orderStall(r)),
      hallNameKey(orderHall(r)),
      r.ServiceID,
      r.ExhRegistrationID,
      r.VendorID,
      r.WorkOrderID,
    ].join("|");
    const prev = latest.get(id);
    if (!prev || Number(r.HMWorkupdationID || 0) > Number(prev.HMWorkupdationID || 0)) {
      latest.set(id, r);
    }
  });
  return Array.from(latest.values());
};

// data (result set 0) lists each hall's zones as parallel CSV columns.
export const expandHallZoneMaster = (rows) => {
  const out = [];
  const seen = new Set();
  (rows || []).forEach((h) => {
    const ids = String(h.ZoneID ?? "").split(",");
    const names = String(h.ZoneName ?? "").split(",");
    names.forEach((name, i) => {
      const zone = name.trim();
      const key = zoneKeyOf(h.HallName, zone);
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push({
        HallZoneMappingID: (ids[i] || "").trim() || key,
        Zone: zone,
        HallID: h.HallID,
        HallName: h.HallName,
      });
    });
  });
  return out;
};

// Booth name -> the rows that belong to it. A row joins on the loose key, only
// within the booth's own hall (rows with no known hall are let through), and an
// exact spelling beats a punctuation-stripped one.
export const attachRowsToBooths = (booths, rows, getStall, getHall) => {
  const byKey = new Map();
  (rows || []).forEach((r) => {
    const key = normalizeBoothKey(getStall(r));
    if (!key) return;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(r);
  });

  const result = new Map();
  (booths || []).forEach((b) => {
    const candidates = byKey.get(normalizeBoothKey(b.BoothName));
    if (!candidates) return;
    const boothHall = hallNameKey(b.HallName);
    const sameHall = candidates.filter((r) => {
      const h = hallNameKey(getHall(r));
      return !h || !boothHall || h === boothHall;
    });
    if (sameHall.length === 0) return;
    const exact = sameHall.filter((r) => stallNameKey(getStall(r)) === b.BoothName);
    result.set(b.BoothName, exact.length > 0 ? exact : sameHall);
  });
  return result;
};

// ─── Service partner deliverables (sp_get_ExhVendorTaskDetails) ─────────
// One row per deliverable x checklist sub point. A deliverable planned on a
// stall (ExhVendorPlanDtlID) belongs to the tracker task with the same stall,
// service and service partner. Hall is only a tie-break: the plan's HallName
// does not always agree with the tracker (H1A6 is planned under "Hall 14").
// Rows with DailyRoutineHallChecklistMappingID are daily routine items.
const isOn = (v) => v === 1 || v === true;
const taskKeyOf = (stall, serviceId, vendorId) =>
  `${normalizeBoothKey(stall)}|${serviceId}|${vendorId}`;

const deliverableStatus = (d) => {
  if (d.completed) return "Completed";
  if ((overdueMinutes(d.overdue) || 0) > 0) return "Delayed";
  if (d.subPoints.some((sp) => sp.done)) return "InProgress";
  return "Pending";
};

export const buildDeliverableIndex = (rows) => {
  const plans = new Map();
  const scope = new Map();
  (rows || []).forEach((r) => {
    if (r.DailyRoutineHallChecklistMappingID != null) return;
    const svcVendor = `${r.ServiceID}|${r.VendorID}`;
    if (r.DeliverablesServiceName) {
      if (!scope.has(svcVendor)) scope.set(svcVendor, new Map());
      const id = r.WorkOrderDeliverablesID != null ? r.WorkOrderDeliverablesID : r.DeliverablesServiceName;
      if (!scope.get(svcVendor).has(id)) {
        scope.get(svcVendor).set(id, { id, name: r.DeliverablesServiceName.trim(), srno: r.Srno });
      }
    }
    if (!r.StallName || r.ExhVendorPlanDtlID == null) return;
    let p = plans.get(r.ExhVendorPlanDtlID);
    if (!p) {
      p = {
        id: r.ExhVendorPlanDtlID,
        deliverableId: r.WorkOrderDeliverablesID != null ? r.WorkOrderDeliverablesID : r.DeliverablesServiceName,
        key: taskKeyOf(r.StallName, r.ServiceID, r.VendorID),
        hall: hallNameKey(r.HallName),
        name: String(r.DeliverablesServiceName || "Deliverable").trim(),
        srno: r.Srno,
        start: r.StartTime || r.StartDateTime,
        end: r.EndTime || r.EndDateTime,
        completed: isOn(r.IsCompleted),
        completedAt: r.CompletionDateTime,
        overdue: r.Overdue,
        hallManagerApproved: isOn(r.IsHallManagerApprove),
        organizerApproved: isOn(r.IsAdminApprove),
        zoneManagerApproved: isOn(r.IsZoneManagerApprove),
        subPoints: [],
      };
      plans.set(p.id, p);
    }
    const spName = String(r.SubPoint || "").trim();
    const spId = r.ExhVendorPointDtlID != null ? r.ExhVendorPointDtlID : spName;
    if (spName && !p.subPoints.some((sp) => sp.id === spId)) {
      p.subPoints.push({ id: spId, name: spName, done: isOn(r.IsCompletedPoint) });
    }
  });

  // A deliverable is sometimes planned twice on the same stall (253 of 2739
  // on UAT, identical but for the plan line id). Keep one per deliverable:
  // a completed copy if any, else the newest line.
  const byTask = new Map();
  plans.forEach((p) => {
    p.status = deliverableStatus(p);
    const pastDue = overdueMinutes(p.overdue);
    p.overdue = !p.completed && pastDue > 0 ? p.overdue : null;
    if (!byTask.has(p.key)) byTask.set(p.key, new Map());
    const perTask = byTask.get(p.key);
    const prev = perTask.get(p.deliverableId);
    const better = !prev ||
      (p.completed && !prev.completed) ||
      (p.completed === prev.completed && Number(p.id) > Number(prev.id));
    if (better) perTask.set(p.deliverableId, p);
  });
  const bySrno = (a, b) => (a.srno || 0) - (b.srno || 0);
  byTask.forEach((perTask, key) => byTask.set(key, Array.from(perTask.values()).sort(bySrno)));
  const scopeLists = new Map();
  scope.forEach((m, k) => scopeLists.set(k, Array.from(m.values()).sort(bySrno)));
  return { byTask, scope: scopeLists };
};

// The deliverables planned for one tracker task, and the service's full
// deliverable list from the work order (what is due even where nothing is
// planned on this stall yet).
export const deliverablesForTask = (index, stall, hall, serviceId, vendorId) => {
  if (!index) return { planned: [], scope: [] };
  const all = index.byTask.get(taskKeyOf(stall, serviceId, vendorId)) || [];
  const sameHall = all.filter((p) => p.hall === hallNameKey(hall));
  return {
    planned: sameHall.length > 0 ? sameHall : all,
    scope: index.scope.get(`${serviceId}|${vendorId}`) || [],
  };
};

// ─── Stall possession (data4) ────────────────────────────────────────────
// One row per exhibitor per stall, graded by Status / StallPossessionStatusID.
// Seen in the 2026-09 capture: 5 = "NDC Pending", 1 = "NDC Issued",
// 2 = "Stall Possession". "Possession Pending" and "No Show" are on the
// organizer's possession screen but did not occur in that capture, so they
// are matched by name only.
export const NO_POSSESSION_RECORD = "No Record";

// Worst first. A stall shared by several exhibitors reads as its least
// advanced one, so "Stall Possession" means every exhibitor has taken it.
export const POSSESSION_ORDER = [
  "No Show",
  "NDC Pending",
  "NDC Issued",
  "Possession Pending",
  "Stall Possession",
];

const POSSESSION_BY_ID = { 1: "NDC Issued", 2: "Stall Possession", 5: "NDC Pending" };

export const possessionStatusOf = (row) => {
  const named = String(row.Status || "").trim().toLowerCase();
  const byName = POSSESSION_ORDER.find((s) => s.toLowerCase() === named);
  if (byName) return byName;
  return POSSESSION_BY_ID[Number(row.StallPossessionStatusID)] || null;
};

export const rollupPossession = (statuses) => {
  let worst = null;
  statuses.forEach((s) => {
    const i = POSSESSION_ORDER.indexOf(s);
    if (i < 0) return;
    if (worst === null || i < POSSESSION_ORDER.indexOf(worst)) worst = s;
  });
  return worst;
};

// The same exhibitor can repeat on a stall (H3-D1F2 carries exhibitor 6572
// six times). Keep one row per hall + stall + exhibitor - the most advanced,
// since a repeat is a later record of the same possession, not a second one.
export const cleanPossessionRows = (rows) => {
  const best = new Map();
  (rows || []).forEach((r) => {
    const stall = stallNameKey(r.StallName);
    if (!stall) return;
    const status = possessionStatusOf(r);
    if (!status) return;
    const id = [hallNameKey(r.HallName), stall, r.ExhRegistrationID].join("|");
    const prev = best.get(id);
    if (!prev || POSSESSION_ORDER.indexOf(status) > POSSESSION_ORDER.indexOf(prev.status)) {
      best.set(id, { ...r, PossessionStatus: status, status });
    }
  });
  return Array.from(best.values()).map(({ status, ...row }) => row);
};
